// cpq2-owned hide rules for ANSWER OPTIONS, merged into the loaded questions.
//
// A configurator sometimes has to narrow a dropdown in a way its NetSuite export does not
// say. Flame Cells is the case this was built for: Core ID/OD lists sixteen sizes, but four
// of them are the core-pipe sizes that only a STD/LN core pipe uses, and the other twelve are
// only ever raw pipe/tube/rod stock. Offering all sixteen for every core type meant every
// picker had to know which was which.
//
// WHY IT WRITES parsed_hide_rule RATHER THAN FILTERING SOMEWHERE. That field is already the
// one thing the whole app agrees on: Question.tsx filters the option out of the widget,
// validation's answerVisible stops counting the question as answerable, and TableWidget reads
// the column equivalent. Writing the rule there means a cpq2-owned rule behaves exactly like
// a NetSuite-authored one everywhere, present and future, instead of being honoured in the
// two places someone remembered to wire up.
//
// WHY NOT EDIT questions.json. scripts/extract-product.cjs rewrites that folder wholesale on
// the next import, so the edit would vanish with nothing failing -- the dropdown would simply
// go back to sixteen sizes. The rules live in the product profile (src/products/) for the
// same reason hiddenGroupCodesByTab does.
import { getProfile } from "../products";
import type { Question } from "../types";

/** OR two parsed-rule expressions. Either saying "hide" hides, so a cpq2 rule can only ever
 *  narrow what the export already allows -- it cannot reveal an option NetSuite hides. That
 *  direction matters for Oversized Small / Oversized Large, which carry a cell-OD rule of
 *  their own: the option shows only where BOTH agree, never because cpq2 asked. */
function orRules(existing: string, added: string): string {
  const a = (existing ?? "").trim();
  if (!a || a === "false") return added;
  if (a === "true") return "true"; // already always hidden; nothing to add
  return `(${a})||(${added})`;
}

/**
 * Lift the export's UNCONDITIONAL hide from the options a product names (answerReveals), IN
 * PLACE. The one direction orRules cannot go.
 *
 * NDB is the case this was built for: the export hides the HD ignitor type with the always-hide rule (star, slash, star), yet
 * the legacy Configuration tab records it on 84 sales orders since 2022, so the grey pre-fill
 * could never offer what those customers ordered (Kevin, 2026-10-18). Only "true" is lifted;
 * an option hidden under a condition keeps it, and a rule naming an option that is not hidden
 * at all counts nothing, so the return value says how many were really revealed.
 */
export function applyAnswerReveals(product: string, questions: Question[]): number {
  const rules = getProfile(product).answerReveals ?? [];
  if (rules.length === 0) return 0;
  let touched = 0;
  for (const rule of rules) {
    const q = questions.find((x) => x.custrecord_cpqc_question_code === rule.question);
    if (!q || !Array.isArray(q._answers)) continue;
    const named = new Set(rule.answers);
    for (const a of q._answers) {
      if (!named.has(a.custrecord_cpqc_answer_code)) continue;
      if ((a.custrecord_cpqc_answer_parsed_hide_rule ?? "").trim() !== "true") continue;
      a.custrecord_cpqc_answer_parsed_hide_rule = "";
      a.custrecord_cpqc_answer_hide_rule = "";
      touched++;
    }
  }
  return touched;
}

/**
 * Apply a product's answerHideRules to its questions, IN PLACE.
 *
 * Returns how many options were narrowed, which is what a caller logs and a test asserts on:
 * a rule naming a question or an option code that does not exist would otherwise do nothing,
 * silently, and look exactly like a rule that was applied.
 */
export function applyAnswerHideRules(product: string, questions: Question[]): number {
  const rules = getProfile(product).answerHideRules;
  if (rules.length === 0) return 0;
  let touched = 0;
  for (const rule of rules) {
    const q = questions.find((x) => x.custrecord_cpqc_question_code === rule.question);
    if (!q || !Array.isArray(q._answers)) continue;
    const named = new Set(rule.answers);
    for (const a of q._answers) {
      const isNamed = named.has(a.custrecord_cpqc_answer_code);
      if (rule.otherAnswers ? isNamed : !isNamed) continue;
      a.custrecord_cpqc_answer_parsed_hide_rule = orRules(
        a.custrecord_cpqc_answer_parsed_hide_rule,
        rule.hiddenWhen
      );
      touched++;
    }
  }
  return touched;
}
